import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Typography, Table, Tag, Spin, Card, Collapse, Toast, Space, Tabs, DatePicker, Select, Modal, Form } from '@douyinfe/semi-ui';
import { IconSearch, IconCopy, IconDownload, IconFilter, IconPlus, IconUser, IconEyeOpened, IconEyeClosedSolid, IconStop } from '@douyinfe/semi-icons';
import { API } from '../helpers';
import { stringToColor } from '../helpers/render';
import Papa from 'papaparse';

const { Text } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const KEY_REGEX = /^sk-[a-zA-Z0-9]{48}$/;

const LogsTable = () => {
    const [apikeyInput, setApikeyInput] = useState('');
    const [apikeys, setApikeys] = useState(() => {
        try {
            const saved = localStorage.getItem('apikeys');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });
    const [activeTabKey, setActiveTabKey] = useState('');
    const [tabData, setTabData] = useState({});
    const [loading, setLoading] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [activeKeys, setActiveKeys] = useState([]);
    const [baseUrl, setBaseUrl] = useState('');
    const baseUrls = JSON.parse(process.env.REACT_APP_BASE_URL);
    const apiServer = process.env.REACT_APP_API_SERVER || 'http://localhost:3001';

    // 从用户名导入 Modal 状态
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [importUsername, setImportUsername] = useState('');
    const [importPassword, setImportPassword] = useState('');
    const [importLoading, setImportLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [importedUsername, setImportedUsername] = useState(() => {
        return localStorage.getItem('importedUsername') || '';
    });
    const [keyNameMap, setKeyNameMap] = useState(() => {
        try {
            const saved = localStorage.getItem('keyNameMap');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });

    // 筛选条件状态
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedModel, setSelectedModel] = useState('all');
    const [modelOptions, setModelOptions] = useState([{ value: 'all', label: '全部模型' }]);
    const [selectedTokenName, setSelectedTokenName] = useState('all');
    const [selectedGroup, setSelectedGroup] = useState('all');
    const [selectedLogType, setSelectedLogType] = useState('all');
    const [selectedDate, setSelectedDate] = useState('all');
    const [queryMode, setQueryMode] = useState('daily'); // 'daily' 或 'detail'

    // 查询进度状态
    const [queryProgress, setQueryProgress] = useState({ current: 0, total: 0, percentage: 0, currentKey: '' });
    const [isQuerying, setIsQuerying] = useState(false);

    // 使用 ref 来实现即时的停止控制
    const stopRef = useRef(false);
    const currentEventSourceRef = useRef(null);

    // 错误横幅状态
    const [errorBanner, setErrorBanner] = useState({ visible: false, message: '' });

    useEffect(() => {
        const firstKey = Object.keys(baseUrls)[0];
        setActiveTabKey(firstKey);
        setBaseUrl(baseUrls[firstKey]);
    }, []);

    useEffect(() => {
        localStorage.setItem('apikeys', JSON.stringify(apikeys));
    }, [apikeys]);

    useEffect(() => {
        localStorage.setItem('importedUsername', importedUsername);
    }, [importedUsername]);

    useEffect(() => {
        localStorage.setItem('keyNameMap', JSON.stringify(keyNameMap));
    }, [keyNameMap]);

    const handleTabChange = (key) => {
        setActiveTabKey(key);
        setBaseUrl(baseUrls[key]);
        setStartDate(null);
        setEndDate(null);
        setSelectedModel('all');
    };

    const addApikey = () => {
        const key = apikeyInput.trim();
        if (!KEY_REGEX.test(key)) {
            Toast.error('令牌格式非法！');
            return;
        }
        if (apikeys.includes(key)) {
            Toast.warning('该令牌已添加');
            return;
        }
        setApikeys([...apikeys, key]);
        setApikeyInput('');
    };

    const removeApikey = (key) => {
        setApikeys(apikeys.filter(k => k !== key));
    };

    const importByUsername = async () => {
        if (!importUsername.trim()) {
            Toast.error('请输入用户名');
            return;
        }
        setImportLoading(true);
        try {
            const params = new URLSearchParams({ username: importUsername.trim(), password: importPassword });
            const res = await API.get(`${apiServer}/api/keys-by-username?${params.toString()}`);
            if (!res.data.success) {
                Toast.error(res.data.message || '查询失败');
                setImportLoading(false);
                return;
            }
            const fetchedKeys = res.data.data.map(item => item.key);
            const nameMap = {};
            res.data.data.forEach(item => { nameMap[item.key] = item.name; });
            setApikeys(fetchedKeys);
            setKeyNameMap(nameMap);
            setImportedUsername(importUsername.trim());
            Toast.success(`已清空旧令牌，成功导入 ${fetchedKeys.length} 个令牌`);
            setImportModalVisible(false);
            setImportUsername('');
            setImportPassword('');
        } catch (e) {
            Toast.error('导入失败');
        }
        setImportLoading(false);
    };

    const resetTabData = (tabKey) => {
        setTabData((prevData) => ({
            ...prevData,
            [tabKey]: {
                tokenResults: [],
                logs: [],
                total_requests: 0,
                total_dollar_fee: 0,
            }
        }));
    };

    const formatDate = (date, endOfDay) => {
        const d = new Date(date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return endOfDay ? `${y}-${m}-${day} 23:59:59` : `${y}-${m}-${day} 00:00:00`;
    };

    const fetchData = async () => {
        console.log('[fetchData] 开始查询');
        if (apikeys.length === 0) {
            Toast.warning('请先添加至少一个令牌，再进行查询');
            return;
        }
        if (!startDate || !endDate) {
            Toast.warning('请先选择开始日期和结束日期，再进行查询');
            return;
        }

        // 按条查询时，检查日期是否为同一天
        if (queryMode === 'detail') {
            const start = new Date(startDate);
            const end = new Date(endDate);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
            if (start.getTime() !== end.getTime()) {
                Toast.warning('按条查询模式下，开始日期和结束日期必须为同一天');
                return;
            }
        }

        console.log('[fetchData] 设置状态');
        setLoading(true);
        setIsQuerying(true);
        stopRef.current = false;
        stopRef.currentReject = null;
        currentEventSourceRef.current = null;

        const startTime = formatDate(startDate, false);
        const endTime = formatDate(endDate, true);

        // 初始化累积数据
        let allLogs = [];
        let tokenResults = [];

        // 初始化进度
        setQueryProgress({
            current: 0,
            total: apikeys.length,
            percentage: 0,
            currentKey: ''
        });

        try {
            // 先获取所有令牌配置信息
            const tokenParams = new URLSearchParams();
            tokenParams.append('keys', apikeys.join(','));
            const tokenRes = await API.get(`${apiServer}/api/token-info?${tokenParams.toString()}`);

            if (!tokenRes.data.success) {
                throw new Error('获取令牌信息失败');
            }

            const tokenInfoList = tokenRes.data.data;
            const tokenInfoMap = {};
            tokenInfoList.forEach(t => {
                tokenInfoMap[t.key] = t;
            });

            // 逐个key查询
            for (let i = 0; i < apikeys.length; i++) {
                // 在循环开始时检查停止标志
                if (stopRef.current) {
                    break;
                }

                const key = apikeys[i];
                const keyName = keyNameMap[key] || `令牌${i + 1}`;

                // 更新进度：显示当前正在查询的key
                setQueryProgress({
                    current: i,
                    total: apikeys.length,
                    percentage: Math.round((i / apikeys.length) * 100),
                    currentKey: keyName
                });

                // 查询单个key的数据
                const params = new URLSearchParams();
                params.append('key', key);
                params.append('start_time', startTime);
                params.append('end_time', endTime);
                params.append('query_mode', queryMode);
                // 如果有导入的用户名，传递给后端
                if (importedUsername) {
                    params.append('username', importedUsername);
                }

                const eventSourceUrl = `${apiServer}/api/logs?${params.toString()}`;

                try {
                    const result = await new Promise((resolve, reject) => {
                        if (stopRef.current) {
                            reject(new Error('stopped'));
                            return;
                        }

                        const eventSource = new EventSource(eventSourceUrl);
                        currentEventSourceRef.current = eventSource;
                        let settled = false;

                        const settle = (fn, val) => {
                            if (settled) return;
                            settled = true;
                            eventSource.close();
                            currentEventSourceRef.current = null;
                            fn(val);
                        };

                        stopRef.currentReject = () => settle(reject, new Error('stopped'));

                        eventSource.onmessage = (event) => {
                            const data = JSON.parse(event.data);

                            if (data.type === 'progress') {
                                // 忽略进度消息
                            } else if (data.type === 'complete') {
                                settle(resolve, data.data);
                            } else if (data.type === 'error') {
                                settle(reject, new Error(data.message));
                            }
                        };

                        eventSource.onerror = (error) => {
                            settle(reject, new Error('连接失败'));
                        };
                    });

                    // 累加日志数据
                    allLogs = allLogs.concat(result.logs);

                    // 计算当前key的费用
                    const keyFee = result.logs.reduce((sum, log) => sum + parseFloat(log.total_dollar_fee || 0), 0);

                    // 添加到tokenResults（直接用原始key查找，不去掉前缀）
                    const tokenInfo = tokenInfoMap[key];
                    if (tokenInfo) {
                        tokenResults.push({
                            ...tokenInfo,
                            total_dollar_fee: keyFee,
                            keyDisplay: `${tokenInfo.key.slice(0, 8)}...${tokenInfo.key.slice(-6)}`,
                        });
                    }
                } catch (error) {
                    // 查询出错，记录错误但继续
                }
            }

            // 无论是否停止，都显示已查询到的数据
            if (allLogs.length > 0) {
                // 计算总计
                const totalRequests = allLogs.reduce((sum, log) => sum + parseInt(log.total_requests || 0), 0);
                const totalDollarFee = allLogs.reduce((sum, log) => sum + parseFloat(log.total_dollar_fee || 0), 0);

                // 获取模型列表
                const modelParams = new URLSearchParams();
                modelParams.append('keys', apikeys.join(','));
                modelParams.append('start_time', startTime);
                modelParams.append('end_time', endTime);
                const modelRes = await API.get(`${apiServer}/api/models?${modelParams.toString()}`);
                const allModels = modelRes.data.success ? modelRes.data.data : [];
                setModelOptions([
                    { value: 'all', label: '全部模型' },
                    ...allModels.map(m => ({ value: m, label: m })),
                ]);

                // 构建新的tab数据
                const newTabData = {
                    ...tabData,
                    [activeTabKey]: { tokenResults, logs: allLogs, total_requests: totalRequests, total_dollar_fee: totalDollarFee },
                };
                setTabData(newTabData);
                setActiveKeys(['1', '2']);

                // 重置筛选条件为默认值
                setSelectedModel('all');
                setSelectedTokenName('all');
                setSelectedGroup('all');
                setSelectedLogType('all');
                setSelectedDate('all');
            } else if (stopRef.current) {
                // 如果停止时还没有查询到任何数据，提示用户
                Toast.warning('停止时尚未查询到任何数据');
            }
        } catch (e) {
            setErrorBanner({ visible: true, message: '查询失败，请重试' });
            resetTabData(activeTabKey);
        }

        setLoading(false);
        setIsQuerying(false);
        setQueryProgress({ current: 0, total: 0, percentage: 0, currentKey: '' });
    };

    const handleStop = () => {
        stopRef.current = true;
        if (stopRef.currentReject) {
            stopRef.currentReject();
            stopRef.currentReject = null;
        }
        Toast.info('正在停止查询...');
    };

    const copyText = async (text) => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                Toast.success('已复制：' + text);
                return;
            }
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                textArea.remove();
                Toast.success('已复制：' + text);
            } catch (err) {
                textArea.remove();
                Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
            }
        } catch (err) {
            Modal.error({ title: '无法复制到剪贴板，请手动复制', content: text });
        }
    };

    const columns = [
        {
            title: queryMode === 'detail' ? '日期时间' : '日期',
            dataIndex: 'stat_date',
            sorter: (a, b) => ('' + a.stat_date).localeCompare(b.stat_date),
            defaultSortOrder: 'ascend',
            fixed: 'left',
            width: queryMode === 'detail' ? 200 : 160,
        },
        {
            title: '类型',
            dataIndex: 'log_type',
            render: (text) => (
                <Tag color={text === '退款' ? 'red' : 'green'} size="large">{text || '消费'}</Tag>
            ),
            sorter: (a, b) => ('' + (a.log_type || '')).localeCompare('' + (b.log_type || '')),
            width: 80,
        },
        {
            title: '模型',
            dataIndex: 'model_name',
            render: (text) => (
                <Tag color={stringToColor(text)} size="large">{text}</Tag>
            ),
            sorter: (a, b) => ('' + a.model_name).localeCompare(b.model_name),
            width: 200,
        },
        {
            title: '令牌名称',
            dataIndex: 'token_name',
            sorter: (a, b) => ('' + a.token_name).localeCompare(b.token_name),
            width: 150,
        },
        {
            title: '分组',
            dataIndex: 'group',
            sorter: (a, b) => ('' + a.group).localeCompare(b.group),
            width: 120,
        },
        {
            title: '档位',
            dataIndex: 'matched_tier',
            render: (text) => text || '-',
            sorter: (a, b) => ('' + (a.matched_tier || '')).localeCompare('' + (b.matched_tier || '')),
            width: 100,
        },
        {
            title: '请求次数',
            dataIndex: 'total_requests',
            sorter: (a, b) => a.total_requests - b.total_requests,
            width: 100,
        },
        {
            title: '按次单价',
            dataIndex: 'unit_price',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.unit_price || 0) - (b.unit_price || 0),
            width: 100,
        },
        {
            title: '提示 Tokens',
            dataIndex: 'total_prompt_tokens',
            sorter: (a, b) => a.total_prompt_tokens - b.total_prompt_tokens,
            width: 120,
        },
        {
            title: '提示单价',
            dataIndex: 'prompt_price',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.prompt_price || 0) - (b.prompt_price || 0),
            width: 100,
        },
        {
            title: '补全 Tokens',
            dataIndex: 'total_completion_tokens',
            sorter: (a, b) => a.total_completion_tokens - b.total_completion_tokens,
            width: 120,
        },
        {
            title: '补全单价',
            dataIndex: 'completion_price',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.completion_price || 0) - (b.completion_price || 0),
            width: 100,
        },
        {
            title: '读缓存',
            dataIndex: 'total_cache_read',
            sorter: (a, b) => a.total_cache_read - b.total_cache_read,
            width: 100,
        },
        {
            title: '读缓存单价',
            dataIndex: 'cache_read_price',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.cache_read_price || 0) - (b.cache_read_price || 0),
            width: 120,
        },
        {
            title: '写缓存',
            dataIndex: 'total_cache_write',
            sorter: (a, b) => a.total_cache_write - b.total_cache_write,
            width: 100,
        },
        {
            title: '写缓存单价',
            dataIndex: 'cache_write_price',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.cache_write_price || 0) - (b.cache_write_price || 0),
            width: 120,
        },
        {
            title: '写缓存1h',
            dataIndex: 'total_cache_write_1h',
            sorter: (a, b) => a.total_cache_write_1h - b.total_cache_write_1h,
            width: 100,
        },
        {
            title: '写缓存1h单价',
            dataIndex: 'cache_write_1h_price',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.cache_write_1h_price || 0) - (b.cache_write_1h_price || 0),
            width: 130,
        },
        {
            title: '图片输入token',
            dataIndex: 'total_image_input',
            sorter: (a, b) => (a.total_image_input || 0) - (b.total_image_input || 0),
            width: 130,
        },
        {
            title: '图片输入单价',
            dataIndex: 'image_input_price',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.image_input_price || 0) - (b.image_input_price || 0),
            width: 130,
        },
        {
            title: '分组倍率',
            dataIndex: 'group_ratio',
            render: (text) => parseFloat(text || 0).toFixed(6),
            sorter: (a, b) => (a.group_ratio || 0) - (b.group_ratio || 0),
            width: 100,
        },
        {
            title: '费用 ($)',
            dataIndex: 'total_dollar_fee',
            render: (text) => parseFloat(text).toFixed(6),
            sorter: (a, b) => a.total_dollar_fee - b.total_dollar_fee,
            fixed: 'right',
            width: 120,
        },
    ];

    const exportCSV = async (e) => {
        e.stopPropagation();
        if (!hasResults) return;

        Toast.info('正在导出CSV文件，请稍候...', { duration: 0, id: 'exporting' });
        setExportLoading(true);
        try {
            // 直接使用前端已有的筛选后数据，并按日期排序
            const sortedLogs = [...filteredLogs].sort((a, b) =>
                ('' + a.stat_date).localeCompare(b.stat_date)
            );
            const csvData = sortedLogs.map(log => ({
                '日期': log.stat_date,
                '类型': log.log_type || '消费',
                '模型': log.model_name,
                '令牌名称': log.token_name,
                '分组': log.group,
                '请求次数': log.total_requests,
                '按次单价': parseFloat(log.unit_price || 0).toFixed(4),
                '提示Tokens': log.total_prompt_tokens,
                '提示单价': parseFloat(log.prompt_price || 0).toFixed(6),
                '补全Tokens': log.total_completion_tokens,
                '补全单价': parseFloat(log.completion_price || 0).toFixed(6),
                '读缓存': log.total_cache_read,
                '读缓存单价': parseFloat(log.cache_read_price || 0).toFixed(6),
                '写缓存': log.total_cache_write,
                '写缓存单价': parseFloat(log.cache_write_price || 0).toFixed(6),
                '写缓存1h': log.total_cache_write_1h,
                '写缓存1h单价': parseFloat(log.cache_write_1h_price || 0).toFixed(6),
                '图片输入token': log.total_image_input || 0,
                '图片输入单价': parseFloat(log.image_input_price || 0).toFixed(6),
                '分组倍率': parseFloat(log.group_ratio || 0).toFixed(2),
                '费用($)': parseFloat(log.total_dollar_fee).toFixed(6),
            }));

            // 计算总费用并添加汇总行
            const totalFee = sortedLogs.reduce((sum, log) => sum + parseFloat(log.total_dollar_fee || 0), 0);
            csvData.push({
                '日期': '',
                '类型': '',
                '模型': '',
                '令牌名称': '',
                '分组': '',
                '请求次数': '',
                '按次单价': '',
                '提示Tokens': '',
                '提示单价': '',
                '补全Tokens': '',
                '补全单价': '',
                '读缓存': '',
                '读缓存单价': '',
                '写缓存': '',
                '写缓存单价': '',
                '写缓存1h': '',
                '写缓存1h单价': '',
                '图片输入token': '',
                '图片输入单价': '',
                '分组倍率': '总计',
                '费用($)': totalFee.toFixed(6),
            });
            const csvString = '\ufeff' + Papa.unparse(csvData);
            const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            // \u4f7f\u7528\u67e5\u8be2\u65e5\u671f\u8303\u56f4\u547d\u540d\u6587\u4ef6
            const formatDate = (date) => {
                if (!date) return '';
                const d = new Date(date);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}${month}${day}`;
            };
            const startDateStr = formatDate(startDate);
            const endDateStr = formatDate(endDate);
            const filename = startDateStr && endDateStr
                ? `\u6570\u636e\u5bfc\u51fa_${startDateStr}-${endDateStr}.csv`
                : 'data.csv';
            link.download = filename;
            if (navigator.userAgent.indexOf('Safari') > -1 && navigator.userAgent.indexOf('Chrome') === -1) {
                link.target = '_blank';
            }
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(url), 100);

            Toast.close('exporting');
        } catch (err) {
            Toast.close('exporting');
            Toast.error('导出失败，请稍后重试');
            console.error('Export failed:', err);
        } finally {
            setExportLoading(false);
        }
    };

    const activeTabData = tabData[activeTabKey] || { tokenResults: [], logs: [], total_requests: 0, total_dollar_fee: 0 };
    const allLogs = activeTabData.logs || [];
    const filteredLogs = allLogs
        .filter(l => selectedModel === 'all' || l.model_name === selectedModel)
        .filter(l => selectedTokenName === 'all' || l.token_name === selectedTokenName)
        .filter(l => selectedGroup === 'all' || l.group === selectedGroup)
        .filter(l => selectedLogType === 'all' || (l.log_type || '消费') === selectedLogType)
        .filter(l => selectedDate === 'all' || l.stat_date === selectedDate);
    const hasResults = activeTabData.tokenResults && activeTabData.tokenResults.length > 0;
    const dateRangeExceeded = startDate && endDate && (new Date(endDate) - new Date(startDate)) / 86400000 > 31;
    const tokenNameOptions = [
        { value: 'all', label: '全部令牌' },
        ...Array.from(new Set(allLogs.map(l => l.token_name))).map(n => ({ value: n, label: n })),
    ];
    const groupOptions = [
        { value: 'all', label: '全部分组' },
        ...Array.from(new Set(allLogs.map(l => l.group).filter(Boolean))).map(g => ({ value: g, label: g })),
    ];
    const logTypeOptions = [
        { value: 'all', label: '全部类型' },
        { value: '消费', label: '消费' },
        { value: '退款', label: '退款' },
    ];
    const dateOptions = [
        { value: 'all', label: '全部日期' },
        ...Array.from(new Set(allLogs.map(l => l.stat_date))).sort().map(d => ({ value: d, label: d })),
    ];
    const filteredTotalRequests = filteredLogs.reduce((s, l) => s + Number(l.total_requests), 0);
    const filteredTotalFee = filteredLogs.reduce((s, l) => s + Number(l.total_dollar_fee), 0);

    const renderContent = () => (
        <>
            <Modal
                title="从用户名导入令牌"
                visible={importModalVisible}
                onCancel={() => { setImportModalVisible(false); setImportUsername(''); setImportPassword(''); setShowPassword(false); }}
                onOk={importByUsername}
                okText="导入"
                cancelText="取消"
                confirmLoading={importLoading}
            >
                <Form layout="vertical">
                    <Form.Input
                        label="用户名"
                        field="username"
                        placeholder="请输入用户名"
                        value={importUsername}
                        onChange={(value) => setImportUsername(value)}
                        autofocus
                    />
                    <Form.Input
                        label="密码"
                        field="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="请输入密码"
                        value={importPassword}
                        onChange={(value) => setImportPassword(value)}
                        suffix={
                            <span style={{ cursor: 'pointer', padding: '0 4px' }} onClick={() => setShowPassword(v => !v)}>
                                {showPassword ? <IconEyeOpened /> : <IconEyeClosedSolid />}
                            </span>
                        }
                    />
                </Form>
            </Modal>
            {/* 错误横幅 */}
            {errorBanner.visible && (
                <div style={{
                    backgroundColor: '#ffebe9',
                    border: '1px solid #ff4d4f',
                    borderRadius: 4,
                    padding: '12px 16px',
                    marginTop: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Text style={{ color: '#ff4d4f', fontWeight: 500 }}>
                        ⚠️ {errorBanner.message}
                    </Text>
                    <Button
                        size="small"
                        type="tertiary"
                        onClick={() => setErrorBanner({ visible: false, message: '' })}
                    >
                        关闭
                    </Button>
                </div>
            )}
            <Card style={{ marginTop: 24 }}>
                {/* 令牌输入区域 */}
                <Input
                    showClear
                    value={apikeyInput}
                    onChange={(value) => setApikeyInput(value)}
                    placeholder="输入令牌 sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx，回车或点击添加"
                    prefix={<IconSearch />}
                    suffix={
                        <Button
                            type='primary'
                            theme="solid"
                            icon={<IconPlus />}
                            onClick={addApikey}
                            disabled={apikeyInput === ''}
                        >
                            添加
                        </Button>
                    }
                    onKeyDown={(e) => { if (e.key === 'Enter') addApikey(); }}
                />
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Button
                        icon={<IconUser />}
                        onClick={() => setImportModalVisible(true)}
                    >
                        从用户名导入
                    </Button>
                    {importedUsername && (
                        <span style={{ backgroundColor: '#d9f7be', color: '#389e0d', padding: '2px 10px', borderRadius: 4, fontWeight: 600 }}>
                            {importedUsername}
                        </span>
                    )}
                </div>
                {/* 已添加的令牌列表 */}
                {apikeys.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <Space wrap>
                            {apikeys.map(key => (
                                <Tag
                                    key={key}
                                    color="blue"
                                    size="large"
                                    closable
                                    onClose={() => removeApikey(key)}
                                >
                                    {keyNameMap[key] || `${key.slice(0, 8)}...${key.slice(-6)}`}
                                </Tag>
                            ))}
                            <Button
                                size="small"
                                type="danger"
                                theme="borderless"
                                onClick={() => { setApikeys([]); setKeyNameMap({}); setImportedUsername(''); Toast.success('已清空所有令牌'); }}
                            >
                                清空全部
                            </Button>
                        </Space>
                    </div>
                )}
                {/* 时间筛选 + 查询按钮 */}
                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <Text type="secondary" style={{ marginRight: 8 }}>开始日期：</Text>
                        <DatePicker
                            value={startDate}
                            onChange={(date) => {
                                setStartDate(date);
                                if (date && endDate) {
                                    const diff = (new Date(endDate) - new Date(date)) / 86400000;
                                    if (diff > 45) { setEndDate(null); Toast.warning('日期跨度不能超过45天'); }
                                }
                            }}
                            placeholder="选择开始日期"
                            style={{ width: 200 }}
                        />
                    </div>
                    <div>
                        <Text type="secondary" style={{ marginRight: 8 }}>结束日期：</Text>
                        <DatePicker
                            value={endDate}
                            onChange={(date) => {
                                if (date && startDate) {
                                    const diff = (new Date(date) - new Date(startDate)) / 86400000;
                                    if (diff > 45) { Toast.warning('日期跨度不能超过45天'); return; }
                                }
                                setEndDate(date);
                            }}
                            placeholder="选择结束日期"
                            style={{ width: 200 }}
                        />
                    </div>
                    <div>
                        <Text type="secondary" style={{ marginRight: 8 }}>查询模式：</Text>
                        <Select
                            value={queryMode}
                            onChange={(value) => setQueryMode(value)}
                            style={{ width: 120 }}
                        >
                            <Select.Option value="daily">按日查询</Select.Option>
                            <Select.Option value="detail">按条查询</Select.Option>
                        </Select>
                    </div>
                    <Button
                        type='primary'
                        theme="solid"
                        icon={<IconSearch />}
                        onClick={fetchData}
                        loading={loading}
                        disabled={apikeys.length === 0 || dateRangeExceeded || isQuerying}
                    >
                        查询
                    </Button>
                    {isQuerying && (
                        <>
                            <Button
                                icon={<IconStop />}
                                onClick={handleStop}
                                type="danger"
                            >
                                停止
                            </Button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Text type="tertiary">
                                    {queryProgress.currentKey ? `正在查询: ${queryProgress.currentKey}` : '查询中...'}
                                </Text>
                                <Text strong>{queryProgress.percentage}%</Text>
                                <Text type="tertiary">({queryProgress.current}/{queryProgress.total})</Text>
                            </div>
                        </>
                    )}
                    <Button
                        icon={<IconFilter />}
                        onClick={() => {
                            setStartDate(null);
                            setEndDate(null);
                            setSelectedModel('all');
                            setSelectedTokenName('all');
                            setSelectedGroup('all');
                            setSelectedLogType('all');
                        }}
                    >
                        重置筛选
                    </Button>
                </div>
                {/* 快捷日期按钮 */}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                        { label: '今天', getRange: () => { const d = new Date(); return [d, d]; } },
                        { label: '昨天', getRange: () => { const d = new Date(); d.setDate(d.getDate() - 1); return [d, new Date(d)]; } },
                        { label: '本周', getRange: () => { const d = new Date(); const day = d.getDay() || 7; const mon = new Date(d); mon.setDate(d.getDate() - day + 1); return [mon, d]; } },
                        { label: '上周', getRange: () => { const d = new Date(); const day = d.getDay() || 7; const mon = new Date(d); mon.setDate(d.getDate() - day - 6); const sun = new Date(d); sun.setDate(d.getDate() - day); return [mon, sun]; } },
                        { label: '本月', getRange: () => { const d = new Date(); return [new Date(d.getFullYear(), d.getMonth(), 1), d]; } },
                        { label: '上月', getRange: () => { const d = new Date(); return [new Date(d.getFullYear(), d.getMonth() - 1, 1), new Date(d.getFullYear(), d.getMonth(), 0)]; } },
                    ].map(({ label, getRange }) => (
                        <Button key={label} size="small" onClick={() => { const [s, e] = getRange(); setStartDate(s); setEndDate(e); }}>
                            {label}
                        </Button>
                    ))}
                </div>
            </Card>
            <Card style={{ marginTop: 24 }}>
                <Collapse activeKey={activeKeys} onChange={(keys) => setActiveKeys(keys)}>
                    {process.env.REACT_APP_SHOW_BALANCE === "true" && (
                        <Panel
                            header="令牌信息"
                            itemKey="1"
                            extra={
                                <Button
                                    icon={<IconDownload />}
                                    theme='borderless'
                                    type='primary'
                                    disabled={!hasResults}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const rows = activeTabData.tokenResults.map(item => ({
                                            '令牌名称': item.name,
                                            '密钥': item.keyDisplay,
                                            '分组': item.group,
                                            '费用($)': parseFloat(item.total_dollar_fee).toFixed(6),
                                            '启用状态': item.statusText,
                                            '创建时间': item.createdTime,
                                        }));
                                        const csv = '\ufeff' + Papa.unparse(rows);
                                        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                                        const url = URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = 'token_info.csv';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        setTimeout(() => URL.revokeObjectURL(url), 100);
                                    }}
                                >
                                    导出为CSV文件
                                </Button>
                            }
                        >
                            <Spin spinning={loading}>
                                {hasResults ? (
                                    <Table
                                        columns={[
                                            { title: '令牌名称', dataIndex: 'name', sorter: (a, b) => ('' + a.name).localeCompare(b.name), width: 120 },
                                            {
                                                title: '密钥',
                                                dataIndex: 'keyDisplay',
                                                width: 180,
                                                render: (text, row) => row._isSummary ? '-' : <Text code>{text}</Text>
                                            },
                                            {
                                                title: '分组',
                                                dataIndex: 'group',
                                                sorter: (a, b) => ('' + a.group).localeCompare(b.group),
                                                render: (text, row) => row._isSummary ? '-' : <Tag color={stringToColor(text)} size="large">{text}</Tag>,
                                                width: 120
                                            },
                                            {
                                                title: '费用 ($)',
                                                dataIndex: 'total_dollar_fee',
                                                render: (text, row) => row._isSummary ? <Text strong style={{ color: '#fa8c16' }}>{parseFloat(text).toFixed(6)}</Text> : parseFloat(text).toFixed(6),
                                                sorter: (a, b) => a.total_dollar_fee - b.total_dollar_fee,
                                                width: 120
                                            },
                                            {
                                                title: '启用状态',
                                                dataIndex: 'statusText',
                                                render: (text, row) => {
                                                    if (row._isSummary) return '-';
                                                    const colorMap = { '已启用': 'green', '已禁用': 'red', '已过期': 'orange', '已耗尽': 'grey' };
                                                    return <Tag color={colorMap[text] || 'default'}>{text}</Tag>;
                                                },
                                                sorter: (a, b) => (a.status || 0) - (b.status || 0),
                                                width: 100
                                            },
                                            {
                                                title: '创建时间',
                                                dataIndex: 'createdTime',
                                                sorter: (a, b) => ('' + a.createdTime).localeCompare(b.createdTime),
                                                render: (text, row) => row._isSummary ? '-' : text,
                                                width: 180
                                            },
                                        ]}
                                        dataSource={[
                                            ...activeTabData.tokenResults,
                                            {
                                                _isSummary: true,
                                                name: <Text strong>汇总</Text>,
                                                keyDisplay: '-',
                                                group: '-',
                                                total_dollar_fee: activeTabData.tokenResults.reduce((s, r) => s + r.total_dollar_fee, 0),
                                                statusText: '-',
                                                createdTime: '-',
                                            }
                                        ]}
                                        rowKey={(r) => r._isSummary ? '__summary__' : r.key}
                                        rowClassName={(r) => r._isSummary ? 'summary-row' : ''}
                                        onRow={(r) => r._isSummary ? { style: { color: '#fa8c16', fontWeight: 600 } } : {}}
                                        pagination={false}
                                        scroll={{ y: 300 }}
                                    />
                                ) : (
                                    <Text type="secondary">暂无数据，请添加令牌后查询</Text>
                                )}
                            </Spin>
                        </Panel>
                    )}
                    {process.env.REACT_APP_SHOW_DETAIL === "true" && (
                        <Panel
                            header="调用详情"
                            itemKey="2"
                            extra={
                                <Button
                                    icon={<IconDownload />}
                                    theme='borderless'
                                    type='primary'
                                    onClick={(e) => exportCSV(e)}
                                    disabled={filteredLogs.length === 0 || exportLoading}
                                    loading={exportLoading}
                                >
                                    {exportLoading ? '正在导出...' : '导出为CSV文件'}
                                </Button>
                            }
                        >
                            <Spin spinning={loading}>
                                <Card style={{ marginBottom: 16, backgroundColor: '#f8f9fa' }}>
                                    <Space wrap>
                                        <div>
                                            <Text type="secondary" style={{ marginRight: 8 }}>日期：</Text>
                                            <Select
                                                value={selectedDate}
                                                onChange={(value) => setSelectedDate(value)}
                                                style={{ width: 200 }}
                                                optionList={dateOptions}
                                            />
                                        </div>
                                        <div>
                                            <Text type="secondary" style={{ marginRight: 8 }}>模型：</Text>
                                            <Select
                                                value={selectedModel}
                                                onChange={(value) => setSelectedModel(value)}
                                                style={{ width: 200 }}
                                                optionList={modelOptions}
                                            />
                                        </div>
                                        <div>
                                            <Text type="secondary" style={{ marginRight: 8 }}>令牌名称：</Text>
                                            <Select
                                                value={selectedTokenName}
                                                onChange={(value) => setSelectedTokenName(value)}
                                                style={{ width: 160 }}
                                                optionList={tokenNameOptions}
                                            />
                                        </div>
                                        <div>
                                            <Text type="secondary" style={{ marginRight: 8 }}>分组：</Text>
                                            <Select
                                                value={selectedGroup}
                                                onChange={(value) => setSelectedGroup(value)}
                                                style={{ width: 160 }}
                                                optionList={groupOptions}
                                            />
                                        </div>
                                        <div>
                                            <Text type="secondary" style={{ marginRight: 8 }}>类型：</Text>
                                            <Select
                                                value={selectedLogType}
                                                onChange={(value) => setSelectedLogType(value)}
                                                style={{ width: 120 }}
                                                optionList={logTypeOptions}
                                            />
                                        </div>
                                    </Space>
                                    <div style={{ marginTop: 16, padding: '12px', backgroundColor: '#fff', borderRadius: '4px' }}>
                                        <Space>
                                            <Tag color="blue" size="large">
                                                共 {filteredTotalRequests} 次请求
                                            </Tag>
                                            <Tag color="orange" size="large">
                                                总费用：${filteredTotalFee.toFixed(4)}
                                            </Tag>
                                        </Space>
                                    </div>
                                </Card>
                                <Table
                                    columns={columns}
                                    dataSource={filteredLogs}
                                    scroll={{ x: 'max-content' }}
                                    pagination={{
                                        pageSize: 50,
                                        hideOnSinglePage: true,
                                        showSizeChanger: true,
                                        pageSizeOpts: [20, 50, 100],
                                        showTotal: (total) => `共 ${total} 条`,
                                        style: { marginTop: 12 },
                                    }}
                                />
                            </Spin>
                        </Panel>
                    )}
                </Collapse>
            </Card>
        </>
    );

    return (
        <>
            {Object.keys(baseUrls).length > 1 ? (
                <Tabs type="line" onChange={handleTabChange}>
                    {Object.entries(baseUrls).map(([key, url]) => (
                        <TabPane tab={key} itemKey={key} key={key}>
                            {renderContent()}
                        </TabPane>
                    ))}
                </Tabs>
            ) : (
                renderContent()
            )}
        </>
    );
};

export default LogsTable;
